import { type WithContextualField } from "@autodromo/contextual-fields";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { CarouselGallery } from "../../components/Gallery/Carousel/CarouselGallery";
import {
  Thumbnails,
  ThumbnailProps,
} from "../../components/Gallery/Thumbnails/Thumbnails";
import { Lightbox } from "../../components/Gallery/Lightbox/Lightbox";
import { GalleryImageConfig } from "../../utils/image/types";
import {
  GallerySectionContainer,
  ThumbnailsContainer,
} from "./DynamicFormWithGallery.styles";

interface GallerySectionProps extends WithContextualField {
  images: GalleryImageConfig[];
  carouselConfig?: {
    navigationType?: 'none' | 'arrows' | 'bar' | 'arrowsAndBar';
    infinite?: boolean;
    autoplay?: boolean;
    autoplaySpeed?: number;
  };
  showThumbnails?: boolean;
  enableLightbox?: boolean;
  elementColor?: string;
  isMobile?: boolean;
  imageAspectRatio?: 'fixed' | 'auto';
}

export const GallerySection = ({
  images,
  carouselConfig,
  showThumbnails = true,
  enableLightbox = true,
  elementColor = "#000000",
  isMobile,
  imageAspectRatio,
  ...contextual
}: GallerySectionProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [maxVisibleThumbnails, setMaxVisibleThumbnails] = useState(5);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setMaxVisibleThumbnails(isMobile || window.innerWidth < 768 ? 3 : 5);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  const imageNodes = useMemo<React.ReactNode[]>(() => {
    return images.map((image) => image.render(image.props));
  }, [images]);

  const handleImageClick = useCallback((index: number) => {
    if (enableLightbox) {
      setLightboxInitialIndex(index);
      setIsLightboxOpen(true);
    }
  }, [enableLightbox]);

  const processedThumbnails = useMemo<ThumbnailProps[]>(() => {
    const hasMoreImages = images.length > maxVisibleThumbnails;
    const thumbnailsToShow = hasMoreImages ? maxVisibleThumbnails - 1 : maxVisibleThumbnails;
    const visibleThumbnails = images.slice(0, thumbnailsToShow);

    const thumbnails: ThumbnailProps[] = visibleThumbnails.map(
      (image, index) => {
        const isVideo =
          image.props.src?.includes(".mp4") ||
          image.props.src?.includes(".webm");

        return {
          type: isVideo ? "video" : "image",
          state: index === activeIndex ? "active" : "default",
          image: image.render(image.props),
          onClick: () => setActiveIndex(index),
          ariaLabel: image.props.alt || `Imagem ${index + 1}`,
        };
      },
    );

    if (hasMoreImages) {
      const remainingCount = images.length - thumbnailsToShow;
      const lastVisibleImage = images[thumbnailsToShow];
      thumbnails.push({
        type: "verMais",
        state: "default",
        image: lastVisibleImage.render(lastVisibleImage.props),
        count: remainingCount,
        ariaLabel: `Ver mais ${remainingCount} itens`,
        onClick: () => handleImageClick(thumbnailsToShow),
      });
    }

    return thumbnails;
  }, [images, activeIndex, maxVisibleThumbnails, handleImageClick]);

  const handleSlideChange = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const handleThumbnailClick = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  if (images.length === 0) {
    return null;
  }

  return (
    <GallerySectionContainer {...contextual}>
      <CarouselGallery
        images={imageNodes}
        imagesPerSlide={1}
        imageGap={0}
        blockWidth="full"
        navigationType={carouselConfig?.navigationType || 'arrowsAndBar'}
        arrowType="outline"
        indicatorType="bullet"
        elementColor={elementColor}
        infinite={carouselConfig?.infinite || false}
        autoplay={carouselConfig?.autoplay || false}
        activeIndex={activeIndex}
        onSlideChange={handleSlideChange}
        dotsOverImage={true}
        onImageClick={handleImageClick}
        imageAspectRatio={imageAspectRatio}
        fixedRatio="3 / 2"
      />

      {showThumbnails && images.length > 1 && (
        <ThumbnailsContainer>
          <Thumbnails
            thumbnails={processedThumbnails}
            onThumbnailClick={handleThumbnailClick}
            hasMoreImages={images.length > maxVisibleThumbnails}
          />
        </ThumbnailsContainer>
      )}

      {enableLightbox && (
        <Lightbox
          images={imageNodes}
          initialIndex={lightboxInitialIndex}
          isOpen={isLightboxOpen}
          onClose={handleCloseLightbox}
        />
      )}
    </GallerySectionContainer>
  );
};
